import React, { memo, useCallback, useMemo } from "react";

import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Select from "react-select";

import "../../css/admin/utils/select-casting.scss";
import default_avatar from "../../resources/default_avatar.png";
import { getElementByUuid } from "../../utils/data/Content";
import { reorderRankedItems } from "../../utils/Utilities";
import { getSelectClassNames } from "../form/Select";
import VirtualizedSelectMenuList from "../form/VirtualizedSelectMenuList";
import { CASTINGS_ROUTE } from "../routes/routes";

import type { DropResult } from "@hello-pangea/dnd";
import type { WithTranslation } from "react-i18next";

import type {
  CastingListItem,
  GetCastingListResponse,
} from "../../api/MediaAPI.types";

interface SelectCastingItem {
  casting_uuid: string;
  type: string;
  rank: number;
}

interface SelectCastingOptionItem extends CastingListItem {
  value: string;
  label: string;
}

interface SelectCastingProps extends WithTranslation {
  disabled?: boolean;
  type: string;
  items: SelectCastingItem[];
  onUpdate: (items: SelectCastingItem[]) => void;
  castings: GetCastingListResponse;
}

const selectComponents = {
  MenuList: VirtualizedSelectMenuList,
};

function SelectCasting({
  disabled = false,
  t,
  type,
  items,
  onUpdate,
  castings,
}: SelectCastingProps): React.JSX.Element {
  const selectedCastingUuidSet = useMemo(
    () =>
      new Set(
        items
          .filter((item) => item.type === type)
          .map((item) => item.casting_uuid),
      ),
    [items, type],
  );

  const availableCastings = useMemo(
    () =>
      castings?.data?.filter(
        (cast) => !selectedCastingUuidSet.has(cast.uuid),
      ) || [],
    [castings?.data, selectedCastingUuidSet],
  );

  const onChange = useCallback(
    (selection: SelectCastingOptionItem | null) => {
      if (selection) {
        const { uuid } = selection;

        const newItems = [...items];
        let maxRank = 0;
        newItems.forEach((it) => {
          if (it.type === type && it.rank > maxRank) maxRank = it.rank;
        });

        newItems.push({
          casting_uuid: uuid,
          type,
          rank: maxRank + 1,
        });

        onUpdate(newItems);
      }
    },
    [items, onUpdate, type],
  );

  const onDelete = useCallback(
    (index: number) => {
      const newItems = [...items];
      newItems.splice(index, 1);
      onUpdate(newItems);
    },
    [items, onUpdate],
  );

  const getCastingCard = (
    item: SelectCastingItem,
    index: number,
    itemType: string,
  ): React.JSX.Element | null => {
    const cast = getElementByUuid(
      item.casting_uuid,
      castings,
    ) as CastingListItem | null;

    if (!cast || item.type !== itemType) return null;

    const castName = cast.name;
    const picture = cast.cover_picture_1_1;

    return (
      <Draggable
        key={index}
        draggableId={item.type + item.rank + index}
        index={index}
        isDragDisabled={false}
      >
        {(provided) => (
          <div
            key={`${itemType}-${item.casting_uuid}`}
            ref={provided.innerRef}
            className="editor-casting-item"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...provided.draggableProps}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...provided.dragHandleProps}
            style={provided.draggableProps.style as React.CSSProperties}
          >
            <div
              className="editor-casting-img"
              style={{
                backgroundImage: `url(${picture ?? default_avatar})`,
              }}
            />
            <Link
              className="hover:text-primary flex text-current hover:cursor-pointer"
              to={`${CASTINGS_ROUTE}/${item.casting_uuid}`}
            >
              <div className="editor-casting-name">{castName}</div>
            </Link>
            {!disabled && (
              <div className="editor-casting-delete">
                <i
                  className="material-icons editor-multi-icons"
                  onClick={() => onDelete(index)}
                >
                  highlight_off
                </i>
              </div>
            )}
          </div>
        )}
      </Draggable>
    );
  };

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const newItems = reorderRankedItems(
        items,
        result.source.index,
        result.destination.index,
      ) as SelectCastingItem[];
      onUpdate(newItems);
    },
    [items, onUpdate],
  );

  const selectClassNames = useMemo(() => getSelectClassNames(), []);

  const castingOptionList = useMemo<SelectCastingOptionItem[]>(
    () =>
      availableCastings.map((casting) => ({
        ...casting,
        value: casting.uuid,
        label: casting.name,
      })),
    [availableCastings],
  );

  return (
    <div className="select-casting-container">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          direction="horizontal"
          droppableId="droppable"
          isDropDisabled={disabled}
        >
          {(provided) => (
            <div ref={provided.innerRef} className="editor-casting-items">
              {items
                .filter((item) => item.type === type)
                .map((item, index) => getCastingCard(item, index, type))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div className="editor-casting-select" style={{ maxWidth: "600px" }}>
        <Select<SelectCastingOptionItem>
          classNamePrefix="slct"
          classNames={selectClassNames}
          components={selectComponents}
          isDisabled={disabled}
          onChange={onChange}
          options={castingOptionList}
          placeholder={t("select")}
        />
      </div>
    </div>
  );
}

export default withTranslation(["editor-smartlist", "languages"])(
  memo(SelectCasting),
);
